import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useBrowseLeads, useBulkLeadStatus } from "../use-leads";
import type { Lead, LeadBrowseParams } from "../types";
import { LeadDetailDrawer } from "./LeadDetailDrawer";

const GRID = "grid-cols-[36px_minmax(0,3fr)_minmax(0,1.4fr)_64px_72px_96px_minmax(0,2fr)]";
const PAGE_SIZE = 25;
const SOURCES = ["ALL", "AI", "PLACES", "AIRDNA"] as const;

// Only real candidates/approvals are actionable; screened-out rows are shown
// (greyed) but never selectable (screens.md §5).
const SELECTABLE = new Set(["CANDIDATE", "APPROVED"]);

/**
 * Server-side Leads browser (ADR-0020): search + filter + pagination + manual
 * selection + bulk approve/reject, with a deep-linkable detail drawer. Shared by
 * the tenant-wide directory and a campaign's Leads tab; pass `campaignId` to lock
 * the browser to one campaign.
 */
export function LeadsBrowser({ campaignId, initialStatus }: { campaignId?: string; initialStatus?: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("lead");

  const [term, setTerm] = useState("");
  const q = useDebounced(term, 300);
  const [status, setStatus] = useState<string>(initialStatus ?? "ALL");
  const [source, setSource] = useState<(typeof SOURCES)[number]>("ALL");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sort, setSort] = useState<"createdAt" | "rating">("createdAt");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Any filter change resets to page 1 and clears the selection (a selection
  // only makes sense against what's currently on screen).
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [q, status, source, verifiedOnly, sort, campaignId]);

  const params: LeadBrowseParams = {
    campaignId,
    q: q || undefined,
    status: status === "ALL" ? undefined : status,
    source: source === "ALL" ? undefined : source,
    hasVerifiedEmail: verifiedOnly || undefined,
    sort,
    dir: sort === "rating" ? "desc" : "desc",
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading, isError, isFetching, refetch } = useBrowseLeads(params);
  const bulk = useBulkLeadStatus();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const counts = useMemo(() => data?.counts ?? {}, [data]);
  const allCount = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);
  const statusKeys = useMemo(
    () => Object.keys(counts).sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b)),
    [counts],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const selectableOnPage = items.filter((v) => SELECTABLE.has(v.status)).map((v) => v.id);
  const allSelected = selectableOnPage.length > 0 && selectableOnPage.every((id) => selected.has(id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      if (selectableOnPage.every((id) => prev.has(id))) return new Set();
      return new Set(selectableOnPage);
    });
  }
  function runBulk(action: "APPROVED" | "REJECTED") {
    const ids = [...selected];
    if (!ids.length) return;
    bulk.mutate({ ids, status: action }, { onSuccess: () => setSelected(new Set()) });
  }

  function openLead(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set("lead", id);
    setSearchParams(next, { replace: false });
  }
  function closeLead() {
    const next = new URLSearchParams(searchParams);
    next.delete("lead");
    setSearchParams(next, { replace: false });
  }

  const selectedCount = selected.size;

  return (
    <div>
      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-[320px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-ge-muted" />
          <Input
            data-testid="leads-search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search name, website, city, email…"
            className="pl-9"
          />
        </div>
        <Segmented
          testId="source-filter"
          options={SOURCES as unknown as string[]}
          value={source}
          onChange={(s) => setSource(s as (typeof SOURCES)[number])}
        />
        <button
          type="button"
          data-testid="verified-filter"
          aria-pressed={verifiedOnly}
          onClick={() => setVerifiedOnly((v) => !v)}
          className={cn(
            "rounded-ge-pill border px-[11px] py-[5px] font-mono text-[11px] transition-colors",
            verifiedOnly
              ? "border-ge-border-accent bg-ge-accent-tint text-ge-accent"
              : "border-ge-border-card text-ge-secondary hover:text-ge-value",
          )}
        >
          verified email
        </button>
        <InfoHint text="Filter to leads with at least one email confirmed deliverable by enrichment." />
        <label className="flex items-center gap-2 rounded-ge-param border border-ge-border-card px-2 py-1 font-mono text-[11px] text-ge-secondary">
          sort
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value as "createdAt" | "rating")}
            className="bg-transparent text-ge-value focus-visible:outline-none"
          >
            <option value="createdAt">newest</option>
            <option value="rating">rating</option>
          </select>
        </label>
      </div>

      {/* Status pills (server counts) */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <InfoHint text="Filter by review status. Screened-out rows (duplicates, already-registered) stay visible with their reason — you only act on real candidates." />
        <FilterPill label="ALL" count={allCount} active={status === "ALL"} onClick={() => setStatus("ALL")} />
        {statusKeys.map((s) => (
          <FilterPill
            key={s}
            label={s}
            count={counts[s] ?? 0}
            tone={pillTone(s)}
            active={status === s}
            onClick={() => setStatus(s)}
          />
        ))}
        {selectedCount > 0 && (
          <div className="ml-auto flex items-center gap-2" data-testid="bulk-bar">
            <span className="font-mono text-[11px] text-ge-secondary">{selectedCount} selected</span>
            <Button variant="accent-outline" size="sm" disabled={bulk.isPending} onClick={() => runBulk("APPROVED")}>
              Approve selected
            </Button>
            <Button variant="outline" size="sm" disabled={bulk.isPending} onClick={() => runBulk("REJECTED")}>
              Reject selected
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="mt-4">
        {isError ? (
          <EmptyState
            message="Couldn't load leads."
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          />
        ) : !isLoading && total === 0 ? (
          <EmptyState message={term || status !== "ALL" || source !== "ALL" || verifiedOnly
            ? "No leads match these filters. Try widening your search."
            : "No leads yet. Run discovery to populate the list."} />
        ) : (
          <TableCard>
            <TableHeadRow grid={GRID}>
              <Th>
                <Checkbox
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={selectableOnPage.length === 0}
                  testId="select-all"
                />
              </Th>
              <Th>Name</Th>
              <Th>City</Th>
              <Th>Rating</Th>
              <Th>Source</Th>
              <Th>Status</Th>
              <Th>Why</Th>
            </TableHeadRow>

            {isLoading ? (
              <SkeletonRows />
            ) : (
              items.map((v) => (
                <LeadRow
                  key={v.id}
                  v={v}
                  selected={selected.has(v.id)}
                  onToggle={() => toggle(v.id)}
                  onOpen={() => openLead(v.id)}
                />
              ))
            )}
          </TableCard>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-3 flex items-center justify-between">
          <p className="font-mono text-[11px] text-ge-muted" data-testid="page-info">
            {from}–{to} of {total}
            {isFetching && <span className="ml-2 text-ge-faint">updating…</span>}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="page-prev"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <span className="font-mono text-[11px] text-ge-secondary">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              data-testid="page-next"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <p className="mt-3 text-[12px] text-ge-muted">
        Screening is visible, not hidden: duplicate and suppressed rows stay listed with their reason,
        greyed and never selectable. Click any lead to see everything we collected.
      </p>

      <LeadDetailDrawer id={openId} onClose={closeLead} />
    </div>
  );
}

const STATUS_ORDER = ["CANDIDATE", "APPROVED", "DUPLICATE", "SUPPRESSED", "REJECTED"];

function pillTone(s: string): "warning" | "danger" | undefined {
  if (s === "DUPLICATE") return "warning";
  if (s === "SUPPRESSED" || s === "REJECTED") return "danger";
  return undefined;
}

function LeadRow({
  v,
  selected,
  onToggle,
  onOpen,
}: {
  v: Lead;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const selectable = SELECTABLE.has(v.status);
  const screenedOut = v.status === "DUPLICATE" || v.status === "SUPPRESSED";
  const verified = v.contacts?.some((c) => c.verified) ?? false;
  return (
    <TableRow
      grid={GRID}
      data-testid={`lead-row-${v.id}`}
      className={cn(screenedOut && "opacity-[.55]", selected && "bg-ge-card")}
    >
      <div onClick={(e) => e.stopPropagation()}>
        {selectable ? <Checkbox checked={selected} onChange={onToggle} testId={`select-${v.id}`} /> : null}
      </div>
      <button
        type="button"
        onClick={onOpen}
        data-testid={`open-${v.id}`}
        className="min-w-0 truncate text-left font-sans text-[13.5px] font-semibold text-ge-primary hover:text-ge-accent"
      >
        {v.name}
        {v.website && <span className="ml-2 font-mono text-[11px] text-ge-faint">{v.website}</span>}
        {verified && <span className="ml-2 font-mono text-[10px] text-ge-accent">✓ email</span>}
      </button>
      <div className="truncate font-mono text-[11.5px] text-ge-value">{v.city?.name ?? <Empty />}</div>
      <Mono>{v.rating != null ? v.rating.toFixed(1) : <Empty />}</Mono>
      <Mono tone={v.source === "AI" ? "accent" : "value"}>{v.source}</Mono>
      <div>
        <StatusText tone={statusTone(v.status)}>{v.status}</StatusText>
      </div>
      <div className="truncate font-mono text-[11px] text-ge-secondary">{v.screenReason ?? <Empty />}</div>
    </TableRow>
  );
}

function statusTone(s: string): "accent" | "value" | "warning" | "danger" | "faint" {
  if (s === "APPROVED") return "accent";
  if (s === "DUPLICATE") return "warning";
  if (s === "SUPPRESSED" || s === "REJECTED") return "danger";
  if (s === "CANDIDATE") return "faint";
  return "value";
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableRow key={i} grid={GRID} aria-hidden data-testid="lead-skeleton">
          <div />
          <div className="h-[14px] w-2/3 animate-pulse rounded bg-ge-border-card" />
          {Array.from({ length: 5 }).map((__, j) => (
            <div key={j} className="h-[12px] w-10 animate-pulse rounded bg-ge-border-card" />
          ))}
        </TableRow>
      ))}
    </>
  );
}

function Mono({ children, tone = "value" }: { children: React.ReactNode; tone?: "accent" | "value" }) {
  return (
    <div className={cn("font-mono text-[11.5px]", tone === "accent" ? "text-ge-accent" : "text-ge-value")}>
      {children}
    </div>
  );
}

function Segmented({
  options,
  value,
  onChange,
  testId,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  testId?: string;
}) {
  return (
    <div className="inline-flex rounded-ge-param border border-ge-border-card p-[2px]" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          aria-pressed={value === o}
          className={cn(
            "rounded-[6px] px-2 py-1 font-mono text-[11px] transition-colors",
            value === o ? "bg-ge-accent-tint text-ge-accent" : "text-ge-secondary hover:text-ge-value",
          )}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function FilterPill({
  label,
  count,
  active,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone?: "warning" | "danger";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-ge-pill px-[11px] py-[5px] font-mono text-[11px] transition-colors",
        active
          ? "border border-ge-border-accent bg-ge-accent-tint text-ge-accent"
          : tone === "warning"
            ? "border border-dashed border-ge-border-warn text-ge-warning"
            : tone === "danger"
              ? "border border-dashed border-ge-border-danger text-ge-danger"
              : "border border-ge-border-card text-ge-secondary hover:text-ge-value",
      )}
    >
      {label} {count}
    </button>
  );
}

function Checkbox({
  checked,
  onChange,
  disabled,
  testId,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      aria-pressed={checked}
      data-testid={testId}
      className={cn("block", disabled && "opacity-30")}
    >
      {checked ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="var(--ge-accent)" stroke="var(--ge-accent)" strokeWidth="2">
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M8 12l3 3 5-6" fill="none" stroke="var(--ge-on-accent)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ge-text-ghost)" strokeWidth="2">
          <rect x="4" y="4" width="16" height="16" rx="3" />
        </svg>
      )}
    </button>
  );
}
